import { getSupportTicketQueryOptions } from "@packages/api-client/hooks/supportController";
import type { GetSupportTicket200 } from "@packages/api-client/types/supportController";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
	ArrowLeft,
	Calendar,
	MessageSquare,
	Send,
	Ticket,
	User,
} from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAddSupportTicketComment } from "@/api/supportTickets";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useDateFormatter } from "@/hooks/useDateFormatter";

export const Route = createFileRoute(
	"/_auth/app/_app/help/support/tickets/$ticketId",
)({
	loader: async ({ context, params: { ticketId } }) => {
		context.queryClient.ensureQueryData(getSupportTicketQueryOptions(ticketId));

		return {
			ticketId,
		};
	},
	component: SupportTicketDetailPage,
});

function SupportTicketDetailPage() {
	const { t } = useTranslation();
	const { formatDate, formatDateTime } = useDateFormatter();
	const { ticketId } = Route.useLoaderData();
	const commentId = useId();

	// Fetch ticket data
	const { data: ticket } = useSuspenseQuery(
		getSupportTicketQueryOptions(ticketId),
	);

	// Comment form state
	const [commentContent, setCommentContent] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);

	// Mutation for adding comments
	const addCommentMutation = useAddSupportTicketComment();

	// Status badge component
	const getStatusBadge = (status: string) => {
		const statusConfig = {
			open: { color: "bg-blue-100 text-blue-800", label: t("help.statusOpen") },
			waiting_for_user: {
				color: "bg-yellow-100 text-yellow-800",
				label: t("help.statusWaitingForUser"),
			},
			waiting_for_staff: {
				color: "bg-orange-100 text-orange-800",
				label: t("help.statusWaitingForStaff"),
			},
			resolved: {
				color: "bg-green-100 text-green-800",
				label: t("help.statusResolved"),
			},
			closed: {
				color: "bg-gray-100 text-gray-800",
				label: t("help.statusClosed"),
			},
		};

		const config =
			statusConfig[status as keyof typeof statusConfig] ?? statusConfig.open;
		return (
			<span
				className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}
			>
				{config.label}
			</span>
		);
	};

	// Type badge component
	const getTypeBadge = (type: string) => {
		const typeConfig = {
			request: {
				color: "bg-blue-100 text-blue-800",
				label: t("help.supportTypeRequest"),
			},
			suggestion: {
				color: "bg-purple-100 text-purple-800",
				label: t("help.supportTypeSuggestion"),
			},
			complaint: {
				color: "bg-red-100 text-red-800",
				label: t("help.supportTypeComplaint"),
			},
		};

		const config =
			typeConfig[type as keyof typeof typeConfig] ?? typeConfig.request;
		return (
			<span
				className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}
			>
				{config.label}
			</span>
		);
	};

	// Handle comment submission
	const handleCommentSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!commentContent.trim()) return;

		setIsSubmitting(true);

		try {
			await addCommentMutation.mutateAsync({
				id: ticketId,
				data: { content: commentContent.trim() },
			});

			// Reset form
			setCommentContent("");
		} catch (error) {
			console.error("Error adding comment:", error);
		} finally {
			setIsSubmitting(false);
		}
	};

	// Render comment component
	const renderComment = (comment: GetSupportTicket200["comments"][number]) => {
		return (
			<div
				key={comment.id}
				className="flex space-x-3 p-4 border-b border-base-200 last:border-b-0"
			>
				<div className="shrink-0">
					<div className="w-8 h-8 rounded-full bg-base-300 flex items-center justify-center">
						<User className="w-4 h-4 text-base-content/70" />
					</div>
				</div>
				<div className="flex-1 min-w-0">
					<div className="flex items-center space-x-2 mb-1">
						<span className="text-sm font-medium text-base-content">
							{comment.isStaff ? t("help.supportTeam") : t("help.you")}
						</span>
						{comment.isStaff && (
							<span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary">
								{t("help.staff")}
							</span>
						)}
						<span className="text-xs text-base-content/70">
							{formatDateTime(new Date(comment.createdAt).getTime())}
						</span>
					</div>
					<div className="text-sm text-base-content whitespace-pre-wrap">
						{comment.content}
					</div>
				</div>
			</div>
		);
	};

	return (
		<div className="h-full flex flex-col overflow-auto">
			{/* Header */}
			<div className="shrink-0 p-6 border-b border-base-300">
				<div className="flex items-center justify-between">
					<div className="flex items-center space-x-4">
						<Link
							to="/app/help/support/tickets"
							className="btn btn-ghost btn-sm"
						>
							<ArrowLeft className="h-4 w-4" />
							{t("common.back")}
						</Link>
						<div>
							<h1 className="text-2xl font-bold text-base-content">
								{ticket.title}
							</h1>
							<p className="text-base-content/70">
								{t("help.ticketDetails")} #{ticket.id.slice(-8)}
							</p>
						</div>
					</div>
					<div className="flex items-center space-x-2">
						{getTypeBadge(ticket.type)}
						{getStatusBadge(ticket.status)}
					</div>
				</div>
			</div>

			{/* Content */}
			<div className="flex-1 min-h-0 p-6 space-y-6">
				{/* Ticket Details Card */}
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center space-x-2">
							<Ticket className="h-5 w-5" />
							<span>{t("help.ticketInformation")}</span>
						</CardTitle>
					</CardHeader>
					<CardContent className="space-y-4">
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<div>
								<Label className="text-sm font-medium text-base-content/70">
									{t("help.supportFieldType")}
								</Label>
								<div className="mt-1">{getTypeBadge(ticket.type)}</div>
							</div>
							<div>
								<Label className="text-sm font-medium text-base-content/70">
									{t("common.status")}
								</Label>
								<div className="mt-1">{getStatusBadge(ticket.status)}</div>
							</div>
							<div>
								<Label className="text-sm font-medium text-base-content/70">
									{t("common.createdAt")}
								</Label>
								<div className="mt-1 flex items-center space-x-2">
									<Calendar className="h-4 w-4 text-base-content/70" />
									<span className="text-sm">
										{formatDate(new Date(ticket.createdAt).getTime())}
									</span>
								</div>
							</div>
							{ticket.updatedAt !== ticket.createdAt && (
								<div>
									<Label className="text-sm font-medium text-base-content/70">
										{t("common.updatedAt")}
									</Label>
									<div className="mt-1 flex items-center space-x-2">
										<Calendar className="h-4 w-4 text-base-content/70" />
										<span className="text-sm">
											{formatDate(new Date(ticket.updatedAt).getTime())}
										</span>
									</div>
								</div>
							)}
						</div>
						<div>
							<Label className="text-sm font-medium text-base-content/70">
								{t("help.supportFieldDescription")}
							</Label>
							<div className="mt-2 p-3 bg-base-100 rounded-lg border">
								<p className="text-sm text-base-content whitespace-pre-wrap">
									{ticket.description}
								</p>
							</div>
						</div>
					</CardContent>
				</Card>

				{/* Comments Section */}
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center space-x-2">
							<MessageSquare className="h-5 w-5" />
							<span>
								{t("help.comments")} ({ticket.comments.length})
							</span>
						</CardTitle>
					</CardHeader>
					<CardContent className="p-0">
						{/* Comments List */}
						{ticket.comments.length === 0 ? (
							<div className="p-6 text-center">
								<MessageSquare className="h-12 w-12 text-base-content/30 mx-auto mb-4" />
								<p className="text-base-content/70">{t("help.noComments")}</p>
							</div>
						) : (
							<div className="max-h-96 overflow-y-auto">
								{ticket.comments.map((comment) => renderComment(comment))}
							</div>
						)}

						{/* Add Comment Form */}
						<div className="border-t border-base-200 p-4">
							<form onSubmit={handleCommentSubmit} className="space-y-4">
								<div>
									<Label htmlFor={commentId} className="text-sm font-medium">
										{t("help.addComment")}
									</Label>
									<Textarea
										id={commentId}
										value={commentContent}
										onChange={(e) => setCommentContent(e.target.value)}
										placeholder={t("help.commentPlaceholder")}
										rows={3}
										className="mt-1"
										disabled={isSubmitting}
										maxLength={2000}
									/>
									<div className="flex justify-between items-center mt-2">
										<span className="text-xs text-base-content/50">
											{commentContent.length}/2000
										</span>
									</div>
								</div>
								<div className="flex justify-end">
									<Button
										type="submit"
										disabled={!commentContent.trim() || isSubmitting}
										className="flex items-center space-x-2 text-primary-content"
									>
										{isSubmitting ? (
											<>
												<div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
												<span>{t("help.commentSubmitting")}</span>
											</>
										) : (
											<>
												<Send className="h-4 w-4" />
												<span>{t("help.addComment")}</span>
											</>
										)}
									</Button>
								</div>
							</form>
						</div>
					</CardContent>
				</Card>
			</div>
		</div>
	);
}
